import React from 'react';
import { 
  X, BarChart3, Briefcase, CheckCircle2, Clock, 
  AlertCircle, ShieldCheck, TrendingUp, Calendar 
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Cell, PieChart, Pie, LabelList
} from 'recharts';

const THEME = {
  card: "bg-slate-900/90 border border-slate-800 backdrop-blur-md shadow-xl",
  tooltipBg: "#0f172a",
  gridStroke: "#1e293b"
};

const DashboardJudicial = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;

  // Lógica de métricas
  const total = data.length;
  const gestionados = data.filter(c => c.gestionado === 'Gestionado').length;
  const programados = data.filter(c => c.fecha_sesion && c.fecha_sesion.trim() !== "").length;
  const sinGestionar = data.filter(c => !c.gestionado || c.gestionado.toUpperCase() === 'PENDIENTE' || c.gestionado.trim() === "").length;

  const chartData = [
    { name: 'Programados', valor: programados, color: '#3b82f6', grad: 'colorBlue' },
    { name: 'Sin Gestionar', valor: sinGestionar, color: '#ef4444', grad: 'colorRed' },
    { name: 'Gestionados', valor: gestionados, color: '#10b981', grad: 'colorGreen' },
  ];

  const statsCards = [
    { title: 'Total', value: total, icon: Briefcase, color: "from-blue-500 to-cyan-400" },
    { title: 'Gestionados', value: gestionados, icon: CheckCircle2, color: "from-emerald-500 to-teal-400" },
    { title: 'Programados', value: programados, icon: Calendar, color: "from-violet-500 to-purple-400" },
    { title: 'Pendientes', value: sinGestionar, icon: AlertCircle, color: "from-orange-500 to-red-400" }
  ];

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-300">
      <div className="bg-slate-900 border border-slate-800 w-full max-w-5xl max-h-[85vh] rounded-[2rem] shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300 text-slate-100">
        
        {/* Header Compacto */}
        <div className="p-5 border-b border-slate-800 flex justify-between items-center bg-slate-900/50">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-gradient-to-br from-blue-600 to-emerald-600 rounded-xl text-white">
              <BarChart3 size={20} />
            </div>
            <h2 className="text-xl font-black tracking-tight">Resumen Judicial</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-rose-500/10 rounded-full text-slate-500 hover:text-rose-500 transition-all">
            <X size={28} />
          </button>
        </div>

        {/* Contenido con Scroll */}
        <div className="p-6 overflow-y-auto custom-scrollbar space-y-6 bg-slate-950/20">
          
          {/* Tarjetas más pequeñas */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {statsCards.map((card, idx) => (
              <div key={idx} className={`${THEME.card} p-4 rounded-2xl flex items-center gap-4`}>
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${card.color} flex items-center justify-center text-white shrink-0 shadow-lg`}>
                  <card.icon size={18} />
                </div>
                <div>
                  <p className="text-slate-500 text-[9px] font-black uppercase tracking-tighter">{card.title}</p>
                  <p className="text-xl font-black text-white leading-none">{card.value}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            {/* Gráfica de Barras - ALTURA REDUCIDA Y ETIQUETAS */}
            <div className={`lg:col-span-2 ${THEME.card} p-5 rounded-3xl`}>
              <h3 className="text-white text-xs font-bold mb-4 flex items-center gap-2 uppercase tracking-widest opacity-70">
                <TrendingUp size={14} className="text-blue-500" /> Comparativa de Estados
              </h3>
              <div className="h-[220px] w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 20, right: 10, left: -25, bottom: 5 }}>
                    <defs>
                      <linearGradient id="colorBlue" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/><stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/></linearGradient>
                      <linearGradient id="colorRed" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/><stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/></linearGradient>
                      <linearGradient id="colorGreen" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/><stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/></linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={THEME.gridStroke} opacity={0.2} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 10, fontWeight: 700}} />
                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 10}} />
                    <Tooltip cursor={{fill: 'rgba(255,255,255,0.03)'}} contentStyle={{ backgroundColor: THEME.tooltipBg, border: '1px solid #334155', borderRadius: '12px', fontSize: '12px' }} />
                    <Bar dataKey="valor" radius={[8, 8, 0, 0]} barSize={50}>
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={`url(#${entry.grad})`} />
                      ))}
                      {/* ETIQUETAS DE DATOS SOBRE LAS BARRAS */}
                      <LabelList dataKey="valor" position="top" style={{ fill: '#fff', fontSize: '12px', fontWeight: 'bold' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Donut Compacto */}
            <div className={`${THEME.card} p-5 rounded-3xl flex flex-col items-center justify-center`}>
              <h3 className="text-white text-[10px] font-bold mb-2 uppercase tracking-widest opacity-70 text-center">Eficiencia</h3>
              <div className="w-full h-[180px] relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[{ name: 'Gestionados', value: gestionados }, { name: 'Pendientes', value: total - gestionados }]}
                      innerRadius={55}
                      outerRadius={75}
                      dataKey="value"
                      stroke="none"
                      startAngle={90}
                      endAngle={-270}
                    >
                      <Cell fill="#10b981" />
                      <Cell fill="#1e293b" />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-3xl font-black text-white">{Math.round((gestionados/total)*100 || 0)}%</span>
                </div>
              </div>
              <div className="mt-2 flex gap-3 text-[9px] font-bold uppercase text-slate-500">
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-emerald-500"></div> Gestionado</div>
                <div className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-slate-700"></div> Pendiente</div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardJudicial;